/**
 * Glitchlings стиль боевой системы
 * Динамичные 3v3 бои в реальном времени
 */

/**
 * Герои Glitchlings
 */
export const GLITCHLINGS_HEROES = {
  bitcor: {
    id: 'bitcor',
    name: 'Bitcor',
    role: 'Controller',
    maxHp: 3200,
    attack: {
      damage: 80,
      speed: 2000, // мс между выстрелами
      range: 8,
      effect: 'confuse', // на 1.5 сек стреляет в случайном направлении
      description: 'Corrupt Pulse - волна искажённых данных'
    },
    gadget: {
      name: 'Rollback',
      cooldown: 14000, // мс
      effect: 'heal_40_percent',
      description: 'Восстанавливает 40% HP и сбрасывает дебаффы'
    },
    ultimate: {
      name: 'Memory Wipe',
      energyPerHit: 6, // энергия за 100 урона
      activationCost: 100,
      effect: 'aoe_debuff_zone',
      radius: 6,
      duration: 4000, // мс
      description: 'Создаёт зону, где враги теряют баффы и получают -20% к точности'
    },
    visual: '🦉',
    lore: 'Фрагментатор данных - искажает память противников'
  },

  noizling: {
    id: 'noizling',
    name: 'Noizling',
    role: 'Bruiser',
    maxHp: 2900,
    attack: {
      damage: 110,
      speed: 1100,
      range: 6,
      effect: 'stun', // оглушение на 1 сек
      description: 'Static Burst - шар статики'
    },
    gadget: {
      name: 'Echo Trap',
      cooldown: 12000,
      effect: 'knockback_damage',
      description: 'Ловушка отталкивает врагов и наносит 50 урона'
    },
    ultimate: {
      name: 'White Noise',
      energyPerHit: 7,
      activationCost: 100,
      effect: 'triple_wave',
      waves: [5, 7, 9], // радиусы волн
      damage: 60,
      stunDuration: 800,
      description: '3 концентрические волны с оглушением'
    },
    visual: '🔊',
    lore: 'Аудио-демон - статика и оглушение'
  },

  forkbomb: {
    id: 'forkbomb',
    name: 'Forkbomb',
    role: 'Assassin',
    maxHp: 2800,
    attack: {
      damage: 130,
      speed: 1400,
      range: 7,
      effect: 'spawn_minion', // создаёт мини-клон
      minionHp: 30,
      minionLifetime: 3000,
      description: 'Process Fork - граната создаёт клона'
    },
    gadget: {
      name: 'Child Sacrifice',
      cooldown: 15000,
      effect: 'sacrifice_buff',
      description: 'Жертвует клонами → +40% скорость и урон на 4 сек'
    },
    ultimate: {
      name: 'Recursive Flood',
      energyPerHit: 5,
      activationCost: 100,
      effect: 'spawn_clones',
      cloneCount: 2,
      cloneHpPercent: 70,
      cloneDamagePercent: 50,
      cloneLifetime: 6000,
      description: 'Создаёт 2 полноценных клона'
    },
    visual: '💥',
    lore: 'Бомба Вилок - клоны и рекурсия'
  },

  zalgoth: {
    id: 'zalgoth',
    name: 'Zalgoth',
    role: 'Disabler',
    maxHp: 3000,
    attack: {
      damage: 90,
      speed: 1500,
      range: 8,
      effect: 'confuse_targets', // видит союзников как врагов
      duration: 2000,
      description: 'Zalgo Text - искажённые символы'
    },
    gadget: {
      name: 'False Target',
      cooldown: 13000,
      effect: 'create_decoy',
      decoyLifetime: 4000,
      description: 'Создаёт ложную копию себя'
    },
    ultimate: {
      name: 'Corrupt Reality',
      energyPerHit: 8,
      activationCost: 100,
      effect: 'mass_confusion',
      duration: 3500,
      description: 'Все враги атакуют случайных целей'
    },
    visual: '🔮',
    lore: 'Искажённый - текстовая коррупция'
  },

  errorborn: {
    id: 'errorborn',
    name: 'ERRORBORN',
    role: 'Marksman',
    maxHp: 2500,
    attack: {
      damage: 70,
      speed: 1000,
      range: 9,
      effect: 'percent_damage', // +30% от утраченного HP
      percentDamage: 0.3,
      vulnerability: 0.15, // +15% урона от Glitch
      description: 'Kernel Error - 0xDEADBEEF'
    },
    gadget: {
      name: 'Fatal Exception',
      cooldown: 16000,
      effect: 'death_immunity',
      threshold: 0.2, // при HP < 20%
      duration: 1000,
      description: '1 сек неуязвимости при низком HP'
    },
    ultimate: {
      name: '0xDEADBEEF',
      energyPerHit: 10,
      activationCost: 100,
      effect: 'execute',
      executeThreshold: 0.5, // при HP < 50%
      ignoreShields: true,
      description: 'Мгновенный урон = 100% от утраченного HP'
    },
    visual: '☠️',
    lore: 'Первый Цифровой Демон - апокалипсис'
  }
};

/**
 * Создаёт состояние боя в стиле Glitchlings
 */
export function createGlitchlingsBattleState({
  teamA = [],
  teamB = [],
  arena = '/dev/null',
  duration = 120000 // 2 минуты
}) {
  const state = {
    arena,
    startTime: Date.now(),
    duration,
    status: 'in_progress',
    teams: {
      A: teamA.map(hero => ({
        ...hero,
        hp: hero.maxHp,
        energy: 0,
        maxEnergy: 100,
        position: { x: 0, y: 0 },
        statusEffects: [],
        cooldowns: {},
        isActive: true
      })),
      B: teamB.map(hero => ({
        ...hero,
        hp: hero.maxHp,
        energy: 0,
        maxEnergy: 100,
        position: { x: 0, y: 0 },
        statusEffects: [],
        cooldowns: {},
        isActive: true
      }))
    },
    log: [],
    events: []
  };

  return state;
}

/**
 * Применяет атаку героя
 */
export function applyAttack(attacker, target, battleState) {
  const hero = GLITCHLINGS_HEROES[attacker.heroId];
  if (!hero) return null;

  const baseDamage = hero.attack.damage;
  let finalDamage = baseDamage;

  // Применяем эффекты атаки
  const effect = hero.attack.effect;
  const effectResult = {
    type: effect,
    duration: 0,
    applied: false
  };

  switch (effect) {
    case 'percent_damage':
      // ERRORBORN: +30% от утраченного HP
      const lostHp = target.maxHp - target.hp;
      finalDamage += Math.floor(lostHp * hero.attack.percentDamage);
      // Накладываем уязвимость
      target.statusEffects.push({
        type: 'glitch_vulnerability',
        value: hero.attack.vulnerability,
        duration: 2000,
        expiresAt: Date.now() + 2000
      });
      break;

    case 'spawn_minion':
      // Forkbomb: создаёт мини-клон
      effectResult.applied = true;
      effectResult.minion = {
        hp: hero.attack.minionHp,
        maxHp: hero.attack.minionHp,
        lifetime: hero.attack.minionLifetime,
        spawnTime: Date.now()
      };
      break;

    case 'confuse':
      // Bitcor: путаница на 1.5 сек
      effectResult.duration = 1500;
      effectResult.applied = true;
      target.statusEffects.push({
        type: 'confused',
        duration: 1500,
        expiresAt: Date.now() + 1500
      });
      break;

    case 'stun':
      // Noizling: оглушение на 1 сек
      effectResult.duration = 1000;
      effectResult.applied = true;
      target.statusEffects.push({
        type: 'stunned',
        duration: 1000,
        expiresAt: Date.now() + 1000
      });
      break;

    case 'confuse_targets':
      // Zalgoth: видит союзников как врагов
      effectResult.duration = hero.attack.duration;
      effectResult.applied = true;
      target.statusEffects.push({
        type: 'target_confusion',
        duration: hero.attack.duration,
        expiresAt: Date.now() + hero.attack.duration
      });
      break;
  }

  // Наносим урон
  target.hp = Math.max(0, target.hp - finalDamage);

  // Накопление энергии для ульта
  const energyGain = Math.floor((finalDamage / 100) * hero.ultimate.energyPerHit);
  attacker.energy = Math.min(100, attacker.energy + energyGain);

  return {
    damage: finalDamage,
    effect: effectResult,
    energyGained: energyGain
  };
}

/**
 * Применяет гаджет
 */
export function applyGadget(hero, battleState) {
  const heroData = GLITCHLINGS_HEROES[hero.heroId];
  if (!heroData) return null;

  const gadget = heroData.gadget;
  const now = Date.now();

  // Проверяем кулдаун
  if (hero.cooldowns.gadget && hero.cooldowns.gadget > now) {
    return { error: 'Gadget on cooldown' };
  }

  // Устанавливаем кулдаун
  hero.cooldowns.gadget = now + gadget.cooldown;

  const result = {
    type: gadget.effect,
    applied: true
  };

  switch (gadget.effect) {
    case 'heal_40_percent':
      // Bitcor: Rollback
      const healAmount = Math.floor(hero.maxHp * 0.4);
      hero.hp = Math.min(hero.maxHp, hero.hp + healAmount);
      // Сбрасываем дебаффы
      hero.statusEffects = hero.statusEffects.filter(e => e.type !== 'debuff');
      result.healAmount = healAmount;
      break;

    case 'knockback_damage':
      // Noizling: Echo Trap
      result.knockback = true;
      result.damage = 50;
      break;

    case 'sacrifice_buff':
      // Forkbomb: Child Sacrifice
      result.speedBoost = 0.4;
      result.damageBoost = 0.4;
      result.duration = 4000;
      hero.statusEffects.push({
        type: 'sacrifice_buff',
        speed: 0.4,
        damage: 0.4,
        expiresAt: now + 4000
      });
      break;

    case 'create_decoy':
      // Zalgoth: False Target
      result.decoy = {
        lifetime: gadget.decoyLifetime,
        spawnTime: now
      };
      break;

    case 'death_immunity':
      // ERRORBORN: Fatal Exception
      if (hero.hp / hero.maxHp < gadget.threshold) {
        hero.statusEffects.push({
          type: 'invulnerable',
          duration: gadget.duration,
          expiresAt: now + gadget.duration
        });
        result.invulnerable = true;
      }
      break;
  }

  return result;
}

/**
 * Применяет ультимативную способность
 */
export function applyUltimate(hero, battleState) {
  const heroData = GLITCHLINGS_HEROES[hero.heroId];
  if (!heroData) return null;

  const ultimate = heroData.ultimate;

  // Проверяем энергию
  if (hero.energy < ultimate.activationCost) {
    return { error: 'Not enough energy' };
  }

  // Тратим энергию
  hero.energy = 0;

  const result = {
    type: ultimate.effect,
    applied: true
  };

  switch (ultimate.effect) {
    case 'aoe_debuff_zone':
      // Bitcor: Memory Wipe
      result.zone = {
        radius: ultimate.radius,
        duration: ultimate.duration,
        spawnTime: Date.now()
      };
      break;

    case 'triple_wave':
      // Noizling: White Noise
      result.waves = ultimate.waves.map(radius => ({
        radius,
        damage: ultimate.damage,
        stunDuration: ultimate.stunDuration
      }));
      break;

    case 'spawn_clones':
      // Forkbomb: Recursive Flood
      result.clones = Array(ultimate.cloneCount).fill(null).map(() => ({
        hp: Math.floor(hero.maxHp * (ultimate.cloneHpPercent / 100)),
        maxHp: Math.floor(hero.maxHp * (ultimate.cloneHpPercent / 100)),
        damagePercent: ultimate.cloneDamagePercent,
        lifetime: ultimate.cloneLifetime,
        spawnTime: Date.now()
      }));
      break;

    case 'mass_confusion':
      // Zalgoth: Corrupt Reality
      result.duration = ultimate.duration;
      // Применяем ко всем врагам
      const enemyTeam = hero.team === 'A' ? battleState.teams.B : battleState.teams.A;
      enemyTeam.forEach(enemy => {
        enemy.statusEffects.push({
          type: 'mass_confusion',
          duration: ultimate.duration,
          expiresAt: Date.now() + ultimate.duration
        });
      });
      break;

    case 'execute':
      // ERRORBORN: 0xDEADBEEF
      result.execute = true;
      result.ignoreShields = ultimate.ignoreShields;
      break;
  }

  return result;
}

/**
 * Обновляет статус-эффекты (удаляет истёкшие)
 */
export function updateStatusEffects(hero) {
  const now = Date.now();
  hero.statusEffects = hero.statusEffects.filter(effect => {
    if (effect.expiresAt && effect.expiresAt <= now) {
      return false;
    }
    return true;
  });
}

/**
 * Проверяет окончание боя
 */
export function checkBattleEnd(battleState) {
  const teamAAlive = battleState.teams.A.filter(h => h.isActive && h.hp > 0).length;
  const teamBAlive = battleState.teams.B.filter(h => h.isActive && h.hp > 0).length;

  if (teamAAlive === 0) {
    return { winner: 'B', reason: 'team_eliminated' };
  }
  if (teamBAlive === 0) {
    return { winner: 'A', reason: 'team_eliminated' };
  }

  // Проверка времени
  const elapsed = Date.now() - battleState.startTime;
  if (elapsed >= battleState.duration) {
    // Побеждает команда с большим количеством живых
    if (teamAAlive > teamBAlive) {
      return { winner: 'A', reason: 'time_limit' };
    } else if (teamBAlive > teamAAlive) {
      return { winner: 'B', reason: 'time_limit' };
    } else {
      return { winner: null, reason: 'draw' };
    }
  }

  return null;
}

